import { store } from 'redux/store';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { HoldingClass, Range, Share } from 'types';
import { getHoldingClassShares } from 'modules/partners/api/getHoldingClassShares';
import { getPartnerShares } from 'modules/partners/api/getPartnerShares';
import { CustomRange } from '../types/CustomRange';
import { getNewRange } from '../utils/getLastRangeByClass';

type UseGetRangesProps = {
  holdingClassId?: string;
  partnerId?: string;
  editedRanges?: Range[];
  page?: number;
  rowsPerPage?: number;
  isReclassificationByClass: boolean;
  isEntireClassReclassification: boolean;
};

export const useGetRanges = ({
  holdingClassId,
  partnerId,
  page = 1,
  rowsPerPage = 10,
  isReclassificationByClass,
  isEntireClassReclassification,
}: UseGetRangesProps) => {
  const [apiShares, setApiShares] = useState<Share[]>([]);
  const [editedRanges, setEditedRanges] = useState<CustomRange[]>([]);
  const [savedRanges, setSavedRanges] = useState<CustomRange[]>([]);
  const [totalRanges, setTotalRanges] = useState<number>(0);
  const [loadingKeys, setLoadingKeys] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const ranges: CustomRange[] = useMemo(
    () =>
      apiShares.map((share: Share) => {
        const holdingClass = share.shareClass as HoldingClass;

        const key = `${share.partner}-${holdingClass?._id}-${share.from}-${share.to}`;

        const existingEditedRange = [...editedRanges, ...savedRanges].find(
          (r) => r.key === key
        );

        return existingEditedRange
          ? {
              ...share,
              key,
              partner: share.partner as string,
              shareClass: holdingClass?._id,
              newFrom: existingEditedRange.newFrom,
              newTo: existingEditedRange.newTo,
              selectedShareClassOption:
                existingEditedRange.selectedShareClassOption,
            }
          : {
              ...share,
              key,
              partner: share.partner as string,
              shareClass: holdingClass?._id,
              selectedShareClassOption: {
                label: holdingClass?.name,
                value: holdingClass?._id,
              },
            };
      }),
    [apiShares, editedRanges, savedRanges]
  );

  const updateEditedRanges = (
    key: string,
    editedRange: CustomRange,
    newRange: Range,
    isSameHoldingClass: boolean
  ) => {
    const exists = editedRanges.some((range) => range.key === key);

    let newEditedRanges: CustomRange[];

    if (isSameHoldingClass) {
      newEditedRanges = editedRanges.filter((range) => range.key !== key);
      setEditedRanges(newEditedRanges);
      return;
    }

    if (exists) {
      newEditedRanges = editedRanges.map((range) =>
        range.key === key
          ? {
              ...range,
              newFrom: newRange.from,
              newTo: newRange.to,
              selectedShareClassOption: editedRange.selectedShareClassOption,
            }
          : range
      );
    } else {
      newEditedRanges = [
        ...editedRanges,
        {
          ...editedRange,
          newFrom: newRange.from,
          newTo: newRange.to,
          selectedShareClassOption: editedRange.selectedShareClassOption,
        },
      ];
    }

    setEditedRanges(newEditedRanges);
  };

  const handleResetValues = () => {
    setEditedRanges([]);
    setLoadingKeys([]);
    setIsLoading(false);
    setIsError(false);
  };

  const handleEditRange = async (key: string, editedRange: CustomRange) => {
    setIsLoading(true);
    setLoadingKeys((prev) => [...prev, key]);
    setIsError(false);

    try {
      const editedHoldingClassId = editedRange.selectedShareClassOption?.value;
      const originalHoldingClassId = editedRange.shareClass;
      const sharesCount = editedRange.to - editedRange.from + 1;
      const isSameHoldingClass =
        originalHoldingClassId === editedHoldingClassId;

      const classEditedRanges = [...editedRanges, ...savedRanges].filter(
        (range) =>
          range.selectedShareClassOption?.value ===
          editedRange.selectedShareClassOption?.value
      );

      const newRange = await getNewRange({
        holdingClassId: editedHoldingClassId,
        sharesCount,
        classEditedRanges,
      });
      updateEditedRanges(key, editedRange, newRange, isSameHoldingClass);

      setIsLoading(false);
      setLoadingKeys((prev) => prev.filter((loadingKey) => loadingKey !== key));
    } catch (error) {
      setIsError(true);
      setIsLoading(false);
      setLoadingKeys((prev) => prev.filter((loadingKey) => loadingKey !== key));
    }
  };

  const handleSaveRanges = () => {
    setSavedRanges((prev) => [...prev, ...editedRanges]);
    handleResetValues();
  };

  const fetchRangesRanges = useCallback(async () => {
    if (isEntireClassReclassification) {
      setApiShares([]);
      setTotalRanges(0);
      return;
    }
    if (!holdingClassId && isReclassificationByClass) {
      setApiShares([]);
      setTotalRanges(0);
      return;
    }
    if (!partnerId && !isReclassificationByClass) {
      setApiShares([]);
      setTotalRanges(0);
      return;
    }

    setIsLoading(true);
    setIsError(false);

    try {
      let sharesData: any = {};

      if (holdingClassId && isReclassificationByClass) {
        sharesData = await store.dispatch(
          getHoldingClassShares({
            holdingClassId,
            params: { page, items: rowsPerPage },
          })
        );
      } else if (partnerId && !isReclassificationByClass) {
        sharesData = await store.dispatch(
          getPartnerShares({
            partnerId,
            params: { page, items: rowsPerPage },
          })
        );
      }

      setApiShares(sharesData?.shares || []);
      setTotalRanges(sharesData?.total || 0);
    } catch (error) {
      setApiShares([]);
      setTotalRanges(0);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [
    holdingClassId,
    isEntireClassReclassification,
    isReclassificationByClass,
    page,
    partnerId,
    rowsPerPage,
  ]);

  useEffect(() => {
    fetchRangesRanges();
  }, [fetchRangesRanges]);

  return {
    data: { ranges, totalRanges, editedRanges, loadingKeys },
    actions: { handleEditRange, handleResetValues, handleSaveRanges },
    isLoading,
    isError,
  };
};
